import { useState } from "react";
import { Panel, SectionHeader, fmt, fmtDate } from "./DashboardPrimitives";
import { MdOutlineAccountBalance, MdSettings } from "react-icons/md";
import { TbCircleCheck, TbAlertCircle, TbUserCheck, TbArrowRight, TbArrowDownLeft, TbAlertTriangle, TbX } from "react-icons/tb";
import { useFinApi, useFinApiCallback } from "src/hooks/useFinApi";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type StatusCategory = "auto" | "manual" | "unmatched" | "ignored" | "pending";

function statusOf(tx: { pending?: boolean; ignored: boolean; matchedInvoiceIds: string[]; matchedBy: "auto" | "manual" | null }): StatusCategory {
    if (tx.pending) return "pending";
    if (tx.ignored) return "ignored";
    if (tx.matchedInvoiceIds.length > 0) return tx.matchedBy === "manual" ? "manual" : "auto";
    return "unmatched";
}

const PREVIEW_COUNT = 4;

/** Compact dashboard summary card — balance, at-a-glance counts, and a short preview of the
 *  most recent transactions (faded at the bottom to signal there's more on the full page).
 *  The full transaction table and manual-match workflow live on their own page
 *  (see pages/Bank/Bank.tsx).
 *  `canOpenBank`: whether the viewer may open the Bank page — staff can be given only this card
 *  (permBankDashboard), and then the links to /bank lead nowhere. `canConnect`: whether the
 *  viewer can connect an account themselves (Doctor/Admin only, requireBankManage). */
export function FinApiPanel({ refreshTrigger = 0, canOpenBank = true, canConnect = true }: { refreshTrigger?: number; canOpenBank?: boolean; canConnect?: boolean }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { loading, connected, connections, accounts, bankName, recentTransactions, finaliseCallback } = useFinApi(refreshTrigger);
    const multiAccount = connections.length > 1;
    const [duplicateWarning, setDuplicateWarning] = useState<string[] | null>(null);

    // BANKSapi's OAuth-style connect flow redirects back here — keep consuming it from the
    // dashboard regardless of where the full bank workspace itself now lives.
    useFinApiCallback(finaliseCallback, setDuplicateWarning);

    const warningBanner = duplicateWarning && (
        <div className="finapi-duplicate-warning">
            <TbAlertTriangle aria-hidden />
            <span>{t("finapi.duplicateAccountWarning", { names: duplicateWarning.join(", ") })}</span>
            <button type="button" onClick={() => setDuplicateWarning(null)} aria-label={t("popup.close")}>
                <TbX />
            </button>
        </div>
    );

    if (loading) {
        return (
            <Panel className="finapi-panel finapi-panel--summary">
                {warningBanner}
                <SectionHeader title={t("finapi.panelTitle")} />
                <div className="briefing-bank-empty">
                    <span className="finapi-loading__spinner" />
                    {t("finapi.fetching")}
                </div>
            </Panel>
        );
    }

    if (!connected) {
        return (
            <Panel className="finapi-panel finapi-panel--summary">
                {warningBanner}
                <SectionHeader title={t("finapi.panelTitle")} />
                <div className="briefing-bank-connect">
                    <div className="briefing-bank-connect__icon">
                        <MdOutlineAccountBalance size={22} />
                    </div>
                    <p className="briefing-bank-connect__text">{t(canConnect ? "finapi.dashboardNudge" : "finapi.dashboardNudgeStaff")}</p>
                    {canConnect && (
                        <button type="button" className="briefing-bank-connect__btn" onClick={() => navigate("/settings?section=bankpayments")}>
                            <MdSettings size={16} /> {t("finapi.goToSettings")}
                        </button>
                    )}
                </div>
            </Panel>
        );
    }

    // `accounts` is only the first connection's (single-account back-compat shape) — sum every
    // connected doctor's accounts instead.
    const totalBalance = (connections.length ? connections.flatMap((c) => c.accounts) : accounts)
        .reduce((s, a) => s + (a.balance ?? 0), 0);
    const allTxs = [...recentTransactions].sort((a, b) => (b.bookingDate || "").localeCompare(a.bookingDate || ""));
    const autoCount = allTxs.filter((tx) => statusOf(tx) === "auto").length;
    const unmatchedCount = allTxs.filter((tx) => statusOf(tx) === "unmatched").length;
    const reviewedCount = allTxs.filter((tx) => statusOf(tx) === "manual" || statusOf(tx) === "ignored").length;
    const preview = allTxs.slice(0, PREVIEW_COUNT);

    return (
        <Panel className="finapi-panel finapi-panel--summary">
            {warningBanner}
            <div className="section-header">
                <div className="finapi-panel-header">
                    <h3 className="section-header__title">
                        {!multiAccount && bankName ? t("finapi.panelTitleBank", { bank: bankName }) : t("finapi.panelTitle")}
                    </h3>
                    {multiAccount
                        ? <span className="finapi-panel-iban">{t("finapi.multiAccountSummary", { count: connections.length })}</span>
                        : accounts[0]?.iban && <span className="finapi-panel-iban">{accounts[0].iban}</span>}
                </div>
            </div>

            <div className="finapi-balance">
                <span className="finapi-balance__label">{t("finapi.totalBalance")}</span>
                <span className="finapi-balance__value">{fmt(totalBalance)}</span>
            </div>

            <div className="finapi-stats finapi-stats--summary">
                <div className="finapi-stat-card">
                    <span className="finapi-stat-card__icon finapi-stat-card__icon--auto"><TbCircleCheck /></span>
                    <div className="finapi-stat-card__body">
                        <span className="finapi-stat-card__value">{autoCount}</span>
                        <span className="finapi-stat-card__label">{t("finapi.statAutoMatched")}</span>
                    </div>
                </div>
                <div className="finapi-stat-card">
                    <span className="finapi-stat-card__icon finapi-stat-card__icon--unmatched"><TbAlertCircle /></span>
                    <div className="finapi-stat-card__body">
                        <span className="finapi-stat-card__value">{unmatchedCount}</span>
                        <span className="finapi-stat-card__label">{t("finapi.statUnmatched")}</span>
                    </div>
                </div>
                <div className="finapi-stat-card">
                    <span className="finapi-stat-card__icon finapi-stat-card__icon--reviewed"><TbUserCheck /></span>
                    <div className="finapi-stat-card__body">
                        <span className="finapi-stat-card__value">{reviewedCount}</span>
                        <span className="finapi-stat-card__label">{t("finapi.statReviewed")}</span>
                    </div>
                </div>
            </div>

            <div className="finapi-panel__preview">
                <div className="finapi-panel__preview-list">
                    {preview.length === 0 && (
                        <div className="finapi-panel__preview-row finapi-panel__preview-row--placeholder">
                            <span className="finapi-panel__preview-counterpart finapi-panel__preview-counterpart--muted">
                                {t("finapi.noTransactions")}
                            </span>
                        </div>
                    )}
                    {preview.map((tx) => {
                        return (
                            <div key={tx.id} className="finapi-panel__preview-row" onClick={canOpenBank ? () => navigate("/bank") : undefined}>
                                <span className="finapi-panel__preview-date">{fmtDate(tx.bookingDate)}</span>
                                <span className="finapi-panel__preview-counterpart">{tx.counterpartName ?? t("finapi.unknownCounterpart")}</span>
                                {multiAccount && tx.ownerDoctorName && (
                                    <span className="finapi-panel__preview-owner">{tx.ownerDoctorName}</span>
                                )}
                                <span className="finapi-table__amt finapi-panel__preview-amt finapi-table__amt--in">
                                    <TbArrowDownLeft aria-hidden />
                                    + {fmt(tx.amount)}
                                </span>
                            </div>
                        );
                    })}
                    {Array.from({ length: preview.length === 0 ? PREVIEW_COUNT - 1 : PREVIEW_COUNT - preview.length }).map((_, i) => (
                        <div key={`placeholder-${i}`} className="finapi-panel__preview-row finapi-panel__preview-row--placeholder" aria-hidden />
                    ))}
                </div>
                <div className="finapi-panel__preview-fade" aria-hidden />
            </div>

            {canOpenBank && (
                <button type="button" className="finapi-panel__view-all" onClick={() => navigate("/bank")}>
                    {t("finapi.viewAll")} <TbArrowRight />
                </button>
            )}
        </Panel>
    );
}
